import { MultiSelect, Search } from "@carbon/react";
import React, { useState, useEffect, useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import UserCard from "../../../Components/userCard";

// Custom hook for debouncing
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// --- GraphQL Query (Unchanged) ---
const ALL_USERS_QUERY = gql`
    query AllUsers($userTypes: [String!]!, $search: String) {
        allUsers(userTypes: $userTypes, search: $search) {
            __typename
            id
            username
            surname
            lastname
            ... on Teacher {
                classes {
                    id
                    name
                }
                isBoarding
            }
            ... on Student {
                class {
                    id
                    name
                }
                groups {
                    id
                    name
                }
                boarding
            }
        }
    }
`;

// --- Helper Components ---

function NoUsersFound() {
    return (
        <div className="w-full text-center py-20 bg-neutral-50 rounded-lg">
            <h3 className="text-xl font-medium text-neutral-800">
                Keine Nutzer gefunden
            </h3>
            <p className="mt-2 text-base text-neutral-500">
                Es gibt keine Nutzer, die deinen Suchkriterien entsprechen.
            </p>
        </div>
    );
}

// --- Main Component ---
export default function UserManagement() {
    // State and Query logic with debouncing...
    const userGroupItems = [
        { id: "STUDENT", text: "Schüler" },
        { id: "TEACHER", text: "Lehrer" },
        { id: "ADMIN", text: "Administrator" }
    ];
    const [selectedUserGroups, setSelectedUserGroups] = useState(userGroupItems);
    const [searchTerm, setSearchTerm] = useState("");
    
    // Debounce search term to avoid excessive API calls
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    
    const userTypes = selectedUserGroups.map((g) => g.id);
    const { data, loading, error } = useQuery(ALL_USERS_QUERY, {
        variables: { userTypes, search: debouncedSearchTerm },
        // Add caching and fetch policy optimizations
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
    });

    return (
        <section className="w-full">
            <header className="mb-6 flex flex-col md:flex-row justify-between md:items-center gap-4">
                <h1 className="text-3xl font-bold text-neutral-800">
                    Nutzerverwaltung
                </h1>
                <Link
                    to="/admin/users/create"
                    className="mainBtn inline-flex items-center justify-center sm:w-auto w-full text-white px-5 py-2"
                >
                    <svg className="mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                <span>
                    Nutzer erstellen
                </span>
                </Link>
            </header>
            <div className="mb-8 flex flex-col md:flex-row gap-4">
                <Search size="lg" className="flex-grow" placeholder="Nach Vornamen, Nachnamen oder Benutzernamen suchen... (z.B. dikoma15)" labelText="Search users" hideLabel value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                <MultiSelect size="lg" label={"Nach Gruppe filtern"} className="w-full md:w-auto md:min-w-[280px]" items={userGroupItems} hideLabel={true} onChange={(e) => setSelectedUserGroups(e.selectedItems)} itemToString={(item) => (item ? item.text : "")} initialSelectedItems={userGroupItems} />
            </div>
            <main>
                {loading && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, index) => (
                        <div key={index} className="bg-neutral-200 animate-pulse rounded-lg h-24"></div>
                    ))}
                </div>}
                {error && <div className="text-center py-8 text-red-600">Error: {error.message}</div>}
                {data && (!loading) && (
                    data.allUsers.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {data.allUsers.slice(0, 32).map((user) => (
                                <UserCard key={user.id} user={user} />
                            ))}
                        </div>
                    ) : (
                        <NoUsersFound />
                    )
                )}
            </main>
        </section>
    );
}